import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import cx from 'classnames';
import { uid } from 'react-uid';

import Cta from 'core/components/Cta';
import Collapse from 'core/components/Collapse';
import { Icon, ICONS } from 'core/components/Icon/Icon';
import {
	useToggle,
	useToggleById,
	useAppContext,
	useTrackEvent,
} from 'core/hooks';
import { SITECORE_PAGES } from 'core/constants';
import { getProductClassificationLabel } from 'core/utils/global-utils';

import {
	IProps as IAdditionalLinks,
	ProfileLinks,
} from '../AdditionalLinksGroup';
import { MenuItem } from '../../Header.sitecore';
import { BalanceProps } from '../DesktopHeader/DesktopNavItem';
import * as S from './MobileHeader.style';

interface IProps extends IAdditionalLinks {
	menuItems: MenuItem[];
	dataTargetRoot?: string;
	accountSwitcherItem?: MenuItem;
	accountSwitcherIcon?: ICONS;
	showAccountSwitcher: boolean;
	balanceData?: BalanceProps;
	mainHeaderContentRef?: React.RefObject<HTMLElement>;
}

const MobileHeader = ({
	menuItems,
	profile,
	notifications,
	dataTargetRoot = 'mobile--header--nav',
	accountSwitcherItem,
	accountSwitcherIcon,
	showAccountSwitcher = false,
	balanceData,
	mainHeaderContentRef,
}: IProps) => {
	const [isMenuActive, setMenuActive] = useToggle();
	const { pathname } = useLocation();
	const [isSeeAllNotifActive, setIsSeeAllNotifActive] = React.useState(
		pathname === SITECORE_PAGES.MESSAGES,
	);

	const activeLevel1MenuItemId = React.useMemo(() => {
		return menuItems.find(({ url }) => {
			const [level1Pathname] = pathname.match(/(^\/[^\/]*)/) ?? [];

			return url === level1Pathname;
		})?.id;
	}, [pathname]);

	const activeLevel2MenuItemId = React.useMemo(() => {
		const activeLevel2Menu = menuItems.find(
			x => x.id === activeLevel1MenuItemId,
		);

		return activeLevel2Menu?.children?.find(x => x.url === pathname)?.id;
	}, [pathname]);

	const [expandedNavId, setExpandedNavId] = useToggleById(
		activeLevel1MenuItemId,
	);

	const { secondaryMenuProfileLinks } = profile;
	const navigationTitleValue =
		accountSwitcherItem?.navigationTitle?.value ?? '';
	const splitAccountSwitcherTitle = navigationTitleValue.split('|') ?? '';

	React.useEffect(() => {
		setIsSeeAllNotifActive(pathname === SITECORE_PAGES.MESSAGES);
	}, [pathname]);

	const secondaryMenuProfileItemId = React.useMemo(() => {
		return secondaryMenuProfileLinks.find(
			x => x.fields.link.value.href === pathname,
		)?.id;
	}, [pathname]);

	// Analytics spreadsheet:
	// - Section: 'Header'
	// - ID: 1.1.2
	const { trackEvent } = useTrackEvent(
		{
			event_section: 'navigation',
			event_type: 'header',
			event_title: 'authenticated',
		},
		false,
	);

	/**
	 * Track the clicking event and toggle the dropdown off.
	 */
	const handleNavLinkClick = React.useCallback(
		(
			{ navigationTitle, url }: Pick<MenuItem, 'navigationTitle' | 'url'>,
			subItem: MenuItem | null = null,
		) => () => {
			// Analytics spreadsheet:
			// - Section: 'Header'
			// - ID: 1.1.2
			trackEvent({
				event_section: 'navigation',
				event_type: 'header',
				event_title: 'authenticated',
				event_label: `${navigationTitle.value.toLowerCase()}${
					subItem ? ` - ${subItem.navigationTitle.value.toLowerCase()}` : ''
				}`,
				event_trigger: 'click link',
				event_url: subItem ? subItem.url : url,
			});

			setMenuActive(false);
		},
		[setExpandedNavId, setMenuActive, trackEvent],
	);

	const handleSecondaryMenuLinkClick = () => {
		setExpandedNavId('');
		setMenuActive();
	};

	const { handleAccountSwitcherModal } = useAppContext();

	return (
		<S.Wrapper
			isMenuOpen={isMenuActive}
			mainHeaderContentHeight={mainHeaderContentRef?.current?.clientHeight ?? 0}
			isIOS={/iPhone|iPad|iPod/i.test(navigator?.userAgent)}
		>
			<S.ToggleButton
				onClick={setMenuActive}
				data-target-id={`${dataTargetRoot}--toggle-btn`}
				aria-pressed={isMenuActive}
				aria-expanded={isMenuActive}
			>
				<span tabIndex={-1}>
					<Icon
						name={isMenuActive ? ICONS.CLOSE_MENU : ICONS.OPEN_MENU}
						aria-label="expand the navigation menu"
						size={20}
					/>
				</span>
			</S.ToggleButton>
			{/* Show only if user has multiple accounts */}
			{
				<S.AccountSwitcherMobile
					onClick={() => {
						if (showAccountSwitcher) {
							handleAccountSwitcherModal('open');
						} else {
							return false;
						}

						// Analytics spreadsheet:
						// - Section: 'Header'
						// - ID: 1.1.3
						trackEvent({
							event_trigger: 'click link',
							event_label: `account type - ${getProductClassificationLabel(
								balanceData?.productClassification,
							)}`,
						});
					}}
				>
					<S.AccountSwitcherContent>
						<label>{splitAccountSwitcherTitle[0]}</label>
						<label>{splitAccountSwitcherTitle[1]}</label>
					</S.AccountSwitcherContent>
					{showAccountSwitcher && (
						<>
							<S.AccountSwitcherContent>
								<label>Switch</label>
								<Icon
									name={accountSwitcherIcon ?? ICONS.SWITCH_CLOSED}
									size={24}
								/>
							</S.AccountSwitcherContent>
						</>
					)}
				</S.AccountSwitcherMobile>
			}
			<Collapse
				isOpen={isMenuActive}
				className={'mobile--header--collapse-wrapper'}
			>
				<S.Nav
					data-target-id={`${dataTargetRoot}`}
					showAccountSwitcher={showAccountSwitcher}
				>
					<S.PrimaryNavList>
						{menuItems.map(({ id, navigationTitle, url, children }, index) => {
							const shouldExpanded = id === expandedNavId;

							return (
								<S.PrimaryNavListLi
									key={uid(id, index)}
									isLevel2Expanded={index === 0 ? false : shouldExpanded}
								>
									{!isEmpty(children) ? (
										<>
											<S.PrimaryNavListCta
												ctaStyle="plain"
												ctaType="button"
												icon={
													shouldExpanded
														? ICONS.ARROW_BACK
														: ICONS.CHEVRON_RIGHT
												}
												iconAlign={shouldExpanded ? 'left' : 'right'}
												onClick={() => setExpandedNavId(id)}
												aria-pressed={shouldExpanded}
												data-target-id={`${dataTargetRoot}--level1-expand-btn-${id}`}
												iconProps={{ size: 25 }}
												aria-label={`View ${navigationTitle?.value} list`}
												isExpanded={shouldExpanded}
											>
												{navigationTitle?.value}
											</S.PrimaryNavListCta>
											<Collapse isOpen={shouldExpanded}>
												<S.SubNavList>
													{children!.map((subItem, index) => (
														<li
															key={uid(subItem.id, index)}
															className={cx('mobile-nav--item', {
																'-active':
																	activeLevel2MenuItemId === subItem.id,
															})}
														>
															<Link
																to={subItem.url}
																onClick={handleNavLinkClick(
																	{ navigationTitle, url },
																	subItem,
																)}
																data-target-id={`${dataTargetRoot}--level2-link-${subItem.id}`}
															>
																{subItem.navigationTitle.value}
															</Link>
														</li>
													))}
												</S.SubNavList>
											</Collapse>
										</>
									) : (
										<Link
											to={url}
											onClick={handleNavLinkClick({ navigationTitle, url })}
											data-target-id={`${dataTargetRoot}--level1-link-${id}`}
										>
											{navigationTitle.value}
										</Link>
									)}
								</S.PrimaryNavListLi>
							);
						})}
					</S.PrimaryNavList>
				</S.Nav>
				<S.Ul>
					{/* TODO: replace with real notification button */}
					<li
						className={cx({
							active: isSeeAllNotifActive,
						})}
					>
						<Cta
							ctaStyle="plain"
							size="small"
							jssLink={notifications.seeAllNotificationsLinkForMobile}
							icon={notifications.level1NotificationButton.value as any}
							iconProps={{ size: 24 }}
							iconAlign="left"
							onClick={handleSecondaryMenuLinkClick}
							data-target-id={`${dataTargetRoot}--additional-links`}
						/>
					</li>
					<ProfileLinks
						secondaryMenuProfileLinks={secondaryMenuProfileLinks}
						handleClick={handleSecondaryMenuLinkClick}
						dataTargetRoot={`${dataTargetRoot}--additional-links`}
						secondaryMenuProfileItemId={secondaryMenuProfileItemId}
					/>
				</S.Ul>
			</Collapse>
		</S.Wrapper>
	);
};

export default MobileHeader;
